import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export const TermsOfServicePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6 -ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <h1
          className="text-3xl md:text-4xl font-medium text-stone-800 mb-2"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Terms of Service
        </h1>
        <p className="text-stone-500 mb-8">Last Updated: March 2026</p>

        <div className="prose prose-stone max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">1. Acceptance</h2>
            <p className="text-stone-600 leading-relaxed">
              By accessing Mindful Renewal you agree to these terms. If you do not agree, do not use this app.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">2. Service Description</h2>
            <p className="text-stone-600 leading-relaxed">
              Mindful Renewal provides an AI-powered emotional wellness companion. This is NOT a medical service, 
              therapy platform, or crisis intervention tool.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">3. Eligibility</h2>
            <p className="text-stone-600 leading-relaxed">
              You must be 18 or older to use this app. By registering you confirm you meet this requirement.
            </p>
          </section>

          <section className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h2 className="text-xl font-medium text-amber-800 mb-3">4. Not a Medical Service</h2>
            <p className="text-amber-700 leading-relaxed font-medium mb-3">
              IMPORTANT: Nothing in this app constitutes:
            </p>
            <ul className="text-amber-700 space-y-2">
              <li>• Mental health treatment or therapy</li>
              <li>• Medical advice or diagnosis</li>
              <li>• A therapist-client relationship</li>
              <li>• Crisis intervention services</li>
            </ul>
            <p className="text-amber-700 mt-3">
              Always seek professional help for mental health concerns.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">5. User Responsibilities</h2>
            <p className="text-stone-600 leading-relaxed">You agree to:</p>
            <ul className="text-stone-600 space-y-2 mt-2">
              <li>Use this app for personal wellness only</li>
              <li>Not misuse or attempt to manipulate Sage</li>
              <li>Not share another person's information without consent</li>
              <li>Seek professional help if experiencing a mental health crisis</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">6. Data Retention</h2>
            <p className="text-stone-600 leading-relaxed">
              Your data is retained for as long as your account is active. Upon account deletion, your personal data and journal entries are permanently deleted within 30 days.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">7. Crisis Disclosure</h2>
            <p className="text-stone-600 leading-relaxed">
              If you express immediate risk of harm to yourself or others, Sage may encourage you to contact emergency services. As a non-clinical AI platform, Mindful Renewal is not a mandated reporter. However, we reserve the right to act in accordance with applicable law in extreme circumstances.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">8. Subscription & Billing</h2>
            <ul className="text-stone-600 space-y-2">
              <li>Subscriptions auto-renew unless cancelled</li>
              <li>Cancel anytime through your account settings</li>
            </ul>
          </section>

          <section className="mb-8 bg-stone-50 border border-stone-200 rounded-xl p-6">
            <h2 className="text-xl font-medium text-stone-800 mb-3">9. Free Trial & Cancellation Policy</h2>
            <p className="text-stone-600 leading-relaxed mb-4">
              We want you to feel confident in your healing journey before committing.
            </p>
            <ul className="text-stone-600 space-y-2">
              <li><strong>14-Day Free Trial:</strong> All new subscribers receive a 14-day free trial. Your card will not be charged until day 14.</li>
              <li><strong>Cancel Anytime:</strong> You may cancel your subscription at any time during the trial or after. No questions asked.</li>
              <li><strong>How to Cancel:</strong> Cancel through your Settings page or contact us at <a href="mailto:support@mindfulrenewaltx.com" className="text-amber-700 underline">support@mindfulrenewaltx.com</a>.</li>
              <li><strong>After Cancellation:</strong> You will retain access until the end of your current billing period.</li>
            </ul>
          </section>

          <section className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h2 className="text-xl font-medium text-stone-800 mb-3">10. Refund Policy</h2>
            <p className="text-stone-600 leading-relaxed mb-4">
              We offer a <strong>7-day refund</strong> from your first charge date. If you are not satisfied, 
              contact <a href="mailto:support@mindfulrenewal.com" className="text-amber-700 underline">support@mindfulrenewal.com</a> within 
              7 days of being charged.
            </p>
            <ul className="text-stone-600 space-y-2">
              <li><strong>Trial Period:</strong> Trial period charges are non-refundable (no charge during trial).</li>
              <li><strong>Annual Subscriptions:</strong> Refunded prorated within first 30 days only.</li>
              <li><strong>To Request a Refund:</strong> Email <a href="mailto:support@mindfulrenewal.com" className="text-amber-700 underline">support@mindfulrenewal.com</a> with your account email and reason for refund.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">11. Intellectual Property</h2>
            <p className="text-stone-600 leading-relaxed">
              All content, prompts, and materials within Mindful Renewal are owned by Visionary Vault Ventures LLC. 
              You may not reproduce or distribute without written permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">12. Limitation of Liability</h2>
            <p className="text-stone-600 leading-relaxed">
              Visionary Vault Ventures LLC is not liable for:
            </p>
            <ul className="text-stone-600 space-y-2 mt-2">
              <li>Emotional distress arising from app use</li>
              <li>Decisions made based on Sage's responses</li>
              <li>Technical interruptions or data loss</li>
              <li>Any indirect or consequential damages</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">13. Indemnification</h2>
            <p className="text-stone-600 leading-relaxed">
              You agree to indemnify Visionary Vault Ventures LLC against claims arising from your misuse of the app.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">14. Termination</h2>
            <p className="text-stone-600 leading-relaxed">
              We reserve the right to suspend accounts that violate these terms. In cases of serious violations, suspension may occur without prior notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">15. Governing Law</h2>
            <p className="text-stone-600 leading-relaxed">
              These terms are governed by the laws of the State of Texas.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">16. Contact</h2>
            <p className="text-stone-600 leading-relaxed">
              legal@mindfulrenewaltx.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};
